/**
 * SKU Search Enhancement
 * ----------------------
 * Adds automatic SKU search to the storefront search:
 *  1. Main search form: if the query looks like a SKU (no whitespace),
 *     append " OR sku:<term>" so Shopify also matches the SKU field.
 *  2. Predictive search: extends the `resources[options][fields]` param
 *     to include `variants.sku` so suggestions also match SKUs.
 *  3. Cleans up the displayed search term on the results page so the
 *     user only sees what they originally typed.
 */
(function () {
  'use strict';

  // ---------- helpers ----------

  // Heuristic: a SKU is a single token (no whitespace) of length >= 2.
  function isLikelySKU(query) {
    if (!query) return false;
    var trimmed = String(query).trim();
    return trimmed.length >= 2 && !/\s/.test(trimmed);
  }

  // Convert "TERM OR sku:TERM" back to "TERM" for display.
  function extractOriginalTerm(query) {
    if (!query) return query;
    var m = String(query).match(/^(.+?)\s+OR\s+sku:\1\s*$/i);
    return m ? m[1] : query;
  }

  function isSearchAction(action) {
    if (!action) return false;
    try {
      var u = new URL(action, window.location.origin);
      return u.pathname.indexOf('/search') !== -1;
    } catch (e) {
      return action.indexOf('/search') !== -1;
    }
  }

  // ---------- 1. Enhance main search form submission ----------

  document.addEventListener(
    'submit',
    function (e) {
      var form = e.target;
      if (!form || form.tagName !== 'FORM' || form.hasAttribute('data-vareum-search')) return;
      if (!isSearchAction(form.action)) return;

      var input = form.querySelector('input[name="q"]');
      if (!input || !input.value) return;

      var original = input.value.trim();
      if (!original) return;
      // Don't double-enhance if query already contains sku: prefix.
      if (/\bsku:/i.test(original)) return;
      if (!isLikelySKU(original)) return;

      input.value = original + ' OR sku:' + original;
    },
    true
  );

  // ---------- 2. Clean displayed search term on results page ----------

  function cleanSearchTermDisplay() {
    // Reset visible search inputs to the original term.
    var inputs = document.querySelectorAll('input[name="q"]');
    inputs.forEach(function (input) {
      if (!input.value) return;
      var cleaned = extractOriginalTerm(input.value);
      if (cleaned !== input.value) input.value = cleaned;
    });

    // Replace the enhanced query in any visible text on the page.
    var params = new URLSearchParams(window.location.search);
    var q = params.get('q');
    if (!q) return;
    var cleaned = extractOriginalTerm(q);
    if (cleaned === q) return;

    var containers = document.querySelectorAll(
      '.template-search__header, .template-search [role="status"], #ProductCount'
    );
    containers.forEach(function (container) {
      replaceInTextNodes(container, q, cleaned);
    });
  }

  function replaceInTextNodes(node, search, replace) {
    if (!node) return;
    if (node.nodeType === 3) {
      if (node.textContent.indexOf(search) !== -1) {
        node.textContent = node.textContent.split(search).join(replace);
      }
      return;
    }
    if (node.nodeType !== 1) return;
    var children = Array.prototype.slice.call(node.childNodes);
    for (var i = 0; i < children.length; i++) {
      replaceInTextNodes(children[i], search, replace);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', cleanSearchTermDisplay);
  } else {
    cleanSearchTermDisplay();
  }

  // ---------- 3. Predictive search: include variants.sku in fields ----------

  var FIELDS_PARAM = 'resources[options][fields]';
  var DEFAULT_FIELDS = 'title,vendor,product_type,variants.sku,tag';

  function addSkuToFields(url) {
    try {
      var u = new URL(url, window.location.origin);
      var existing = u.searchParams.get(FIELDS_PARAM);
      if (existing) {
        if (existing.indexOf('variants.sku') === -1) {
          u.searchParams.set(FIELDS_PARAM, existing + ',variants.sku');
        }
      } else {
        u.searchParams.set(FIELDS_PARAM, DEFAULT_FIELDS);
      }
      // Preserve absolute vs. relative form of the original url.
      if (/^https?:\/\//i.test(url)) return u.toString();
      return u.pathname + u.search + u.hash;
    } catch (e) {
      if (url.indexOf('variants.sku') !== -1) return url;
      var sep = url.indexOf('?') !== -1 ? '&' : '?';
      return url + sep + encodeURIComponent(FIELDS_PARAM) + '=' + encodeURIComponent(DEFAULT_FIELDS);
    }
  }

  function isPredictiveSearch(url) {
    return typeof url === 'string' && url.indexOf('/search/suggest') !== -1;
  }

  // Patch fetch
  if (window.fetch) {
    var originalFetch = window.fetch;
    window.fetch = function (resource, init) {
      try {
        if (typeof resource === 'string' && isPredictiveSearch(resource)) {
          resource = addSkuToFields(resource);
        } else if (resource && resource.url && isPredictiveSearch(resource.url)) {
          resource = new Request(addSkuToFields(resource.url), resource);
        }
      } catch (e) {
        /* noop */
      }
      return originalFetch.call(this, resource, init);
    };
  }

  // Patch XMLHttpRequest (some predictive-search implementations use XHR)
  if (window.XMLHttpRequest) {
    var origOpen = window.XMLHttpRequest.prototype.open;
    window.XMLHttpRequest.prototype.open = function (method, url) {
      try {
        if (isPredictiveSearch(url)) {
          arguments[1] = addSkuToFields(url);
        }
      } catch (e) {
        /* noop */
      }
      return origOpen.apply(this, arguments);
    };
  }
})();